// ─── ultaAgent.js ─────────────────────────────────────────────────────────────
// Agent for Ulta Beauty (via Anthropic mock).
// Outputs { item, brand, price, rating, inStock, store } + Ulta extras.
// ─────────────────────────────────────────────────────────────────────────────

const ANTHROPIC_API = '/api/anthropic';
const MODEL         = 'claude-haiku-4-5-20251001';

function buildPrompt(query) {
  return `Return ONLY a raw JSON object. No markdown, no explanation, no extra text.

Simulate an Ulta Beauty API search response for: "${query}"

Return exactly 3 products using this schema:
{
  "items": [
    {
      "title": "Full product title",
      "brand": "Brand name",
      "price": 24.99,
      "original_price": 32.00,
      "rating": 4.5,
      "review_count": 3200,
      "sku": "12345678",
      "shade": null,
      "size": "1.0 oz",
      "seller_type": "Ulta",
      "has_ultamate_deal": false,
      "ultamate_label": null,
      "is_prestige": false,
      "is_mass": true,
      "is_salon": false,
      "is_same_day": true,
      "is_store_pickup": true,
      "store_count": 1350,
      "shipping_free": true,
      "shipping_cost": 0,
      "availability": "In Stock"
    }
  ]
}

Rules:
- REAL Ulta Beauty product names, brands, and realistic prices for "${query}"
- Brands: CeraVe, Neutrogena, L'Oreal, Maybelline, NYX, e.l.f., Revlon, Garnier, Olay, Dove, Tarte, Urban Decay, Too Faced, NARS, Charlotte Tilbury, Drunk Elephant, The Ordinary, Fenty Beauty, MAC, Benefit
- is_prestige: true for high-end brands (Charlotte Tilbury, Drunk Elephant, NARS, MAC, Fenty), false for mass brands
- is_mass: true for drugstore brands (CeraVe, Neutrogena, L'Oreal, Maybelline)
- has_ultamate_deal: true for 1 item (Ultamate Rewards member discount), ultamate_label like "21 Days of Beauty: 50% off"
- shade: include for makeup products (e.g. "Medium Beige", "Deep Mahogany"), null for skincare
- size: always include (e.g. "1.0 oz", "240ml", "0.33 fl oz")
- is_same_day: true for 2 items (same-day delivery via DoorDash)
- is_store_pickup: true for all (Ulta has 1350+ stores)
- shipping_free: true if price >= 35
- original_price higher than price for 2 items, same for 1
- Price at low / mid / premium tiers for "${query}"`;
}

async function callAPI(prompt) {
  const response = await fetch(ANTHROPIC_API, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model:      MODEL,
      max_tokens: 2000,
      messages:   [{ role: 'user', content: prompt }],
    }),
  });
  if (!response.ok) throw new Error(`Ulta agent API error: ${response.status}`);
  const data    = await response.json();
  const rawText = data.content?.find(b => b.type === 'text')?.text || '';
  const first   = rawText.indexOf('{');
  const last    = rawText.lastIndexOf('}');
  if (first === -1) throw new Error('Ulta agent: no JSON found');
  return JSON.parse(rawText.slice(first, last + 1));
}

function normalizeItem(raw, store) {
  const price         = parseFloat(raw.price)          || 0;
  const originalPrice = parseFloat(raw.original_price) || null;
  const discountPct   = originalPrice && originalPrice > price
    ? Math.round((1 - price / originalPrice) * 100) : null;

  return {
    item:              raw.title          || 'Unknown Product',
    brand:             raw.brand          || 'Unknown',
    price,
    rating:            parseFloat(raw.rating) || 4.0,
    inStock:           raw.availability   !== 'Out of Stock',
    store:             store.id,

    // Ulta-specific extras
    sku:               raw.sku            || null,
    shade:             raw.shade          || null,
    size:              raw.size           || null,
    original_price:    originalPrice,
    discount_pct:      discountPct,
    review_count:      parseInt(raw.review_count) || 0,
    seller_type:       'Ulta',
    is_prestige:       raw.is_prestige    === true,
    is_mass:           raw.is_mass        === true,
    is_salon:          raw.is_salon       === true,
    has_ultamate_deal: raw.has_ultamate_deal === true,
    ultamate_label:    raw.ultamate_label || null,
    is_same_day:       raw.is_same_day    === true,
    is_store_pickup:   raw.is_store_pickup === true,
    store_count:       parseInt(raw.store_count) || 0,
    shipping_free:     raw.shipping_free  === true,
    shipping_cost:     parseFloat(raw.shipping_cost) || 0,
    total_cost:        +(price + (raw.shipping_free ? 0 : (parseFloat(raw.shipping_cost) || 0))).toFixed(2),
    marketplace:       'ulta',
  };
}

export async function runUltaAgent(store, query) {
  const prompt = buildPrompt(query);
  const parsed = await callAPI(prompt);
  const items  = Array.isArray(parsed.items) ? parsed.items : [];
  return items.map(item => normalizeItem(item, store));
}

export default runUltaAgent;
